import React from 'react';
import { Helmet } from 'react-helmet-async';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { LuPackageCheck, LuSparkles, LuShieldCheck } from 'react-icons/lu';

const clauses = [
  {
    title: 'Bookings & Deposits',
    detail:
      'Orders are confirmed once a 50% advance is received. The balance is payable at delivery or pickup. Deposits are non-refundable once ingredients and décor supplies have been purchased.'
  },
  {
    title: 'Design Approval',
    detail:
      'We share sketches or reference photos for complex cakes. Final design tweaks requested within 48 hours of delivery may incur additional charges or may not be possible depending on progress.'
  },
  {
    title: 'Allergies & Dietary Notes',
    detail:
      'Please disclose any allergies when placing your order. While we maintain strict hygiene, our kitchen uses nuts, dairy, eggs, and wheat, so cross-contamination cannot be fully eliminated.'
  },
  {
    title: 'Force Majeure',
    detail:
      'Unforeseen events such as extreme weather, transport strikes, or power interruptions may delay deliveries. In such cases we will contact you immediately to arrange the best alternative.'
  },
  {
    title: 'Equipment & Dessert Display Items',
    detail:
      'For items provided for your event—such as cake stands, cupcake stands, candy jars, apothecary jars, or other display pieces—customers are responsible for returning them in good condition. Any loss or damage will be charged at the replacement cost. We kindly ask for careful handling so that all items can be reused safely for other celebrations.'
  }
];

const TermsOfService = () => (
  <>
    <Helmet>
      <title>Terms of Service | Nithuja Cakes</title>
      <meta
        name="description"
        content="Understand the booking, payment, equipment, and delivery terms for Nithuja Cakes."
      />
    </Helmet>

    <div className="bg-gray-50 min-h-screen text-gray-800">
      <Navbar />
      <main className="max-w-5xl mx-auto pt-24 pb-16 px-4 sm:px-6 lg:px-8 xl:px-10">
        <p className="text-sm uppercase tracking-[0.35em] text-pink-600">Policies</p>

        <h1 className="mt-3 text-3xl sm:text-4xl font-serif font-bold text-gray-900">
          Terms of Service
        </h1>

        <p className="mt-4 text-lg text-gray-600 leading-relaxed">
          These terms outline how we work together—from booking and creative direction to delivery, equipment usage,
          and after-care. Please read them carefully before placing your order.
        </p>

        <section className="mt-10 rounded-2xl bg-white shadow-sm ring-1 ring-gray-100 p-6 sm:p-7">
          <div className="flex items-start gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-amber-50 text-amber-600">
              <LuShieldCheck className="h-6 w-6" aria-hidden="true" />
            </div>
            <div>
              <p className="text-sm uppercase tracking-[0.3em] text-amber-500">Booking guidelines</p>
              <h2 className="mt-1 text-xl font-semibold text-gray-900">Clear expectations for every celebration</h2>
              <p className="mt-3 text-gray-600 leading-relaxed">
                We keep our process transparent so your cake order feels smooth, predictable, and easy to coordinate.
              </p>
            </div>
          </div>
        </section>

        <div className="mt-8 grid gap-6">
          {clauses.map(({ title, detail }, index) => (
            <section
              key={title}
              className={`rounded-2xl bg-white shadow-sm ring-1 ring-gray-100 p-6 sm:p-7 ${index % 2 === 0 ? 'border-l-4 border-amber-400' : 'border-l-4 border-pink-500'}`}
            >
              <div className="flex items-start gap-4">
                <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl ${index % 2 === 0 ? 'bg-amber-50 text-amber-600' : 'bg-pink-50 text-pink-600'}`}>
                  <LuPackageCheck className="h-5 w-5" aria-hidden="true" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
                  <p className="mt-3 text-gray-600 leading-relaxed">{detail}</p>
                </div>
              </div>
            </section>
          ))}
        </div>

        <section className="mt-8 rounded-2xl bg-amber-50 border border-amber-100 p-6 sm:p-7">
          <div className="flex items-start gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-white text-amber-600 shadow-sm">
              <LuSparkles className="h-6 w-6" aria-hidden="true" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-amber-700">Questions?</h3>
              <p className="mt-2 text-gray-600 leading-relaxed">
                Reach us on WhatsApp
                <a
                  href="https://wa.me/94778590241"
                  className="text-amber-700 font-semibold ml-1"
                >
                  +94 778 590 241
                </a>
                or email
                <a
                  href="mailto:contact.aarasolutions@gmail.com"
                  className="text-amber-700 font-semibold ml-1"
                >
                  contact.aarasolutions@gmail.com
                </a>{' '}
                for clarifications before placing your order.
              </p>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  </>
);

export default TermsOfService;
